import { useState } from "react";
import { post } from "../api";
import { useI18n } from "../i18n";

export function LoginPage({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const [mode, setMode] = useState<"login" | "register" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function executeLogin(targetEmail: string, targetPassword: string) {
    if (!targetEmail.trim() || !targetPassword) {
      setErr(t.common.error);
      return;
    }
    setBusy(true); setErr(null); setMsg(null);
    try {
      const payload: Record<string, string> = { email: targetEmail.trim(), password: targetPassword };
      if (mfaRequired) {
        if (useRecovery) {
          payload.recovery_code = recoveryCode.trim();
        } else {
          payload.totp_code = totpCode.trim();
        }
      }
      const r = await post<{ mfa_required?: boolean; message?: string }>("/api/v1/auth/login", payload);
      if (r && r.mfa_required) {
        setMfaRequired(true);
        setMsg(r.message || t.auth.mfaDesc);
        return;
      }
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (mode === "login") {
      await executeLogin(email, password);
      return;
    }
    setBusy(true); setErr(null); setMsg(null);
    try {
      if (mode === "register") {
        await post("/api/v1/auth/register", { email: email.trim(), password });
        setMsg(t.auth.registerSent);
      } else {
        await post("/api/v1/auth/reset-password/request", { email: email.trim() });
        setMsg(t.auth.resetSent);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const headingTitle = mfaRequired
    ? t.auth.mfaTitle
    : mode === "login"
      ? t.auth.loginTitle
      : mode === "register"
        ? t.auth.registerTitle
        : t.auth.resetTitle;

  return (
    <div className="center">
      <div className="card" style={{ maxWidth: 450, margin: "0 auto", textAlign: "left" }}>
        <h2>{headingTitle}</h2>
        
        {!mfaRequired && (
          <>
            <label>{t.auth.emailLabel}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus
            />
            {mode !== "reset" && (
              <>
                <label>{t.auth.passwordLabel}</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </>
            )}
          </>
        )}

        {mfaRequired && (
          <>
            <p className="muted">{t.auth.mfaDesc}</p>
            {!useRecovery ? (
              <>
                <label>{t.auth.totpLabel}</label>
                <input
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  placeholder={t.auth.totpPlaceholder}
                  maxLength={6}
                  autoFocus
                />
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="ghost"
                    style={{ fontSize: 13 }}
                    onClick={() => setUseRecovery(true)}
                  >
                    {t.auth.useRecovery}
                  </button>
                </div>
              </>
            ) : (
              <>
                <label>{t.auth.recoveryLabel}</label>
                <input
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder={t.auth.recoveryPlaceholder}
                  autoFocus
                />
                <div style={{ marginTop: 8 }}>
                  <button
                    type="button"
                    className="ghost"
                    style={{ fontSize: 13 }}
                    onClick={() => setUseRecovery(false)}
                  >
                    {t.auth.useTotp}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        <div style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button className="primary" disabled={busy} onClick={submit} style={{ minWidth: 100 }}>
            {busy
              ? t.common.submitting
              : mfaRequired
                ? t.auth.verifyAndLogin
                : mode === "login"
                  ? t.auth.loginTitle
                  : t.auth.sendEmail}
          </button>
          
          {mfaRequired ? (
            <button
              className="ghost"
              onClick={() => {
                setMfaRequired(false);
                setTotpCode("");
                setRecoveryCode("");
                setMsg(null);
                setErr(null);
              }}
            >
              {t.auth.cancelRetry}
            </button>
          ) : (
            <>
              <button
                className="ghost"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setMsg(null);
                  setErr(null);
                }}
              >
                {mode === "login" ? t.auth.toRegister : t.auth.toLogin}
              </button>
              {mode === "login" && (
                <button className="ghost" onClick={() => setMode("reset")}>
                  {t.auth.forgotPassword}
                </button>
              )}
            </>
          )}
        </div>
        {msg && <p className="muted" style={{ marginTop: 10 }}>{msg}</p>}
        {err && <p className="error" style={{ marginTop: 10 }}>{err}</p>}
      </div>
    </div>
  );
}
